// src/App.js
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";

import { ModeProvider } from "./context/ModeContext";
import { Navigation }            from "./components/Navigation";
import ErrorBoundary             from "./components/ErrorBoundary";

import { Landing }            from "./pages/Landing";
import { FocusPage }          from "./pages/FocusPage";
import { AnalyzerPage }       from "./pages/AnalyzerPage";
import PerformancePage        from "./pages/PerformancePage";
import AIPlannerPage          from "./pages/AIPlannerPage";
import LoginPage              from "./pages/LoginPage";
import SignupPage             from "./pages/SignupPage";
import { UnifiedPlannerPage } from "./pages/UnifiedPlannerPage";
import { captureOAuthToken }  from "./api/auth";

// ─────────────────────────────────────────────
// Auth guard
// ─────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const token = localStorage.getItem("token");

  if (!token) return <Navigate to="/login" />;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (Date.now() > payload.exp * 1000) {
      localStorage.removeItem("token");
      return <Navigate to="/login" />;
    }
  } catch (e) {
    console.error("Invalid token");
  }

  return children;
}

// ─────────────────────────────────────────────
// Inner app — needs access to ModeContext for AnimatePresence key
// ─────────────────────────────────────────────
function AppInner() {

  useEffect(() => {
    captureOAuthToken();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Navigation />

        {/* Routes — pages animate themselves individually */}
        <Routes>
              {/* Public */}
              <Route path="/"       element={<Landing />} />
              <Route path="/login"  element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />

              {/* Redirects for old routes */}
              <Route path="/todo"      element={<Navigate to="/tasks"      replace />} />
              <Route path="/planner"   element={<Navigate to="/tasks"      replace />} />
              <Route path="/dashboard" element={<Navigate to="/tasks"      replace />} />
              <Route path="/analyzer"  element={<Navigate to="/ai-planner" replace />} />
              <Route path="/about"     element={<Navigate to="/"           replace />} />

              {/* Protected */}
              <Route
                path="/tasks"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary><UnifiedPlannerPage /></ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/focus"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary><FocusPage /></ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-planner"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary><AIPlannerPage /></ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary><PerformancePage /></ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/performance"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary><PerformancePage /></ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/unified-planner"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary><UnifiedPlannerPage /></ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* 404 */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
      </BrowserRouter>
    </div>
  );
}

// ─────────────────────────────────────────────
// Root — ModeProvider wraps everything
// ─────────────────────────────────────────────
function App() {
  return (
    <ModeProvider>
      <AppInner />
    </ModeProvider>
  );
}

export default App;
